import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import { 
  Camera, 
  Monitor, 
  Wifi, 
  Shield,
  CheckCircle,
  Eye,
  Lock,
  Smartphone,
  Router,
  Settings
} from 'lucide-react';

const SurveillancePage = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const services = [
    {
      title: "Advanced CCTV Surveillance Systems",
      description: "State-of-the-art video surveillance solutions with AI-powered analytics and real-time monitoring capabilities.",
      icon: Camera,
      image: "https://images.unsplash.com/photo-1589935447067-5531094415d1",
      features: [
        "4K Ultra HD camera systems",
        "Night vision and infrared capabilities",
        "Motion detection and alerts",
        "Facial recognition technology",
        "Remote monitoring access",
        "Cloud and local storage options"
      ]
    },
    {
      title: "Network Infrastructure Setup",
      description: "Comprehensive networking solutions to support surveillance systems and secure communications.",
      icon: Wifi,
      image: "https://images.pexels.com/photos/7663144/pexels-photo-7663144.jpeg",
      features: [
        "Secure network architecture",
        "High-bandwidth infrastructure",
        "Redundant connectivity options",
        "Network security protocols",
        "VPN and encrypted tunnels",
        "24/7 network monitoring"
      ]
    },
    {
      title: "IP-Based Video Monitoring",
      description: "Advanced network-based surveillance systems with centralized monitoring and intelligent video analytics.",
      icon: Monitor,
      image: "https://images.pexels.com/photos/5475750/pexels-photo-5475750.jpeg",
      features: [
        "Network video recorders (NVR)",
        "Centralized management systems",
        "Real-time video streaming",
        "Automated threat detection",
        "Integration with access control",
        "Mobile app monitoring"
      ]
    },
    {
      title: "Video Door Phone Integration",
      description: "Sophisticated door entry systems with video verification and access control integration.",
      icon: Smartphone,
      image: "https://images.unsplash.com/photo-1705147293087-9e183bf149ef",
      features: [
        "Two-way audio communication",
        "HD video door stations",
        "Remote unlock capabilities",
        "Visitor recording and logging",
        "Integration with security systems",
        "Mobile notifications"
      ]
    }
  ];

  const systemFeatures = [
    {
      title: "AI-Powered Analytics",
      description: "Advanced artificial intelligence for automated threat detection and behavioral analysis",
      icon: Eye
    },
    {
      title: "Secure Transmission",
      description: "Encrypted video streams and secure data transmission protocols",
      icon: Lock
    },
    {
      title: "Remote Access",
      description: "Secure remote monitoring from anywhere with authorized access",
      icon: Router
    },
    {
      title: "Integration Ready",
      description: "Seamless integration with existing security and building management systems",
      icon: Settings
    }
  ];

  const applications = [
    "Government Facilities",
    "Military Installations",
    "Critical Infrastructure",
    "Corporate Headquarters",
    "Data Centers",
    "Embassies & Consulates",
    "Research Facilities",
    "Transportation Hubs"
  ];

  const technologySpecs = [
    {
      category: "Camera Technology",
      specs: [
        "4K Ultra HD Resolution (3840x2160)",
        "Low-light performance (0.001 lux)",
        "Wide dynamic range (120dB+)",
        "Vandal-resistant housing (IK10)",
        "Weather-resistant (IP67/IP68)",
        "Pan-tilt-zoom capabilities"
      ]
    },
    {
      category: "Analytics & AI",
      specs: [
        "Facial recognition accuracy (99.7%)",
        "License plate recognition",
        "Behavioral pattern analysis",
        "Object classification",
        "Crowd density monitoring",
        "Perimeter intrusion detection"
      ]
    },
    {
      category: "Network & Storage",
      specs: [
        "Gigabit Ethernet connectivity",
        "PoE+ power delivery",
        "H.265+ video compression",
        "Edge and cloud storage",
        "Redundant backup systems",
        "Bandwidth optimization"
      ]
    }
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section 
        className="section-padding relative overflow-hidden"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1589935447067-5531094415d1')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-green-900/90 to-near-black/90 z-0" />
        <div className="absolute inset-0 grid-pattern opacity-20 z-1" />
        
        <div className="container relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-700 rounded-16 flex items-center justify-center">
                <Camera className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="heading-font text-4xl md:text-6xl font-bold text-off-white">
                  Surveillance & <span className="text-royal-gold">Monitoring</span>
                </h1>
              </div>
            </div>
            
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed max-w-3xl">
              Comprehensive surveillance solutions with advanced monitoring capabilities, 
              AI-powered analytics, and secure networking infrastructure for critical facilities 
              and high-security environments.
            </p>

          </motion.div>
        </div>
      </section>

      {/* Services Detail */}
      <section className="section-padding bg-gray-dark" ref={ref}>
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="heading-font text-4xl md:text-5xl font-bold mb-6 text-off-white">
              Surveillance <span className="text-royal-gold">Solutions</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Advanced surveillance and monitoring systems designed for government facilities, 
              critical infrastructure, and high-security environments.
            </p>
          </motion.div>

          <div className="space-y-20">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
                }`}
              >
                {/* Content */}
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-700 rounded-16 flex items-center justify-center">
                      <service.icon className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h3 className="heading-font text-3xl md:text-4xl font-bold text-off-white">
                        {service.title}
                      </h3>
                    </div>
                  </div>
                  
                  <p className="text-lg text-gray-300 mb-8 leading-relaxed">
                    {service.description}
                  </p>
                  
                  {/* Features List */}
                  <div className="space-y-3">
                    {service.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center space-x-3">
                        <CheckCircle className="w-5 h-5 text-royal-gold flex-shrink-0" />
                        <span className="text-gray-200">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Image */}
                <div className={index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}>
                  <div className="relative rounded-24 overflow-hidden group">
                    <img 
                      src={service.image} 
                      alt={service.title}
                      className="w-full h-80 object-cover transition-transform duration-300 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-near-black/60 to-transparent" />
                    <div className="absolute bottom-6 left-6">
                      <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-700 rounded-12 flex items-center justify-center">
                        <service.icon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* System Features */}
      <section className="section-padding bg-near-black">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="heading-font text-4xl md:text-5xl font-bold mb-6 text-off-white">
              Advanced <span className="text-royal-gold">Features</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {systemFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="glass-morphism p-6 rounded-16 text-center hover:border-royal-gold transition-all duration-300 group"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-royal-gold to-gold-light rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                  <feature.icon className="w-8 h-8 text-near-black" />
                </div>
                <h3 className="text-lg font-semibold text-off-white mb-2 group-hover:text-royal-gold transition-colors duration-300">
                  {feature.title}
                </h3>
                <p className="text-gray-300 text-sm">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Technical Specifications */}
      <section className="section-padding bg-gray-dark">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="heading-font text-4xl md:text-5xl font-bold mb-6 text-off-white">
              Technical <span className="text-royal-gold">Specifications</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Enterprise-grade surveillance technology with military-standard specifications 
              and government-approved security protocols.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {technologySpecs.map((spec, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="service-card"
              >
                <h3 className="heading-font text-2xl font-semibold text-royal-gold mb-6">
                  {spec.category}
                </h3>
                <div className="space-y-3">
                  {spec.specs.map((item, itemIndex) => (
                    <div key={itemIndex} className="flex items-center space-x-3">
                      <CheckCircle className="w-4 h-4 text-royal-gold flex-shrink-0" />
                      <span className="text-gray-200 text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Applications */}
      {/* <section className="section-padding bg-near-black">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8 }}
            >
              <h2 className="heading-font text-4xl md:text-5xl font-bold mb-6 text-off-white">
                Critical <span className="text-royal-gold">Applications</span>
              </h2>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                Our surveillance systems protect the most sensitive facilities and 
                critical infrastructure nationwide, providing 24/7 security monitoring 
                and threat detection capabilities.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                {applications.map((application, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <Shield className="w-5 h-5 text-royal-gold flex-shrink-0" />
                    <span className="text-gray-200">{application}</span>
                  </div>
                ))}
              </div>

            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="relative rounded-24 overflow-hidden">
                <img 
                  src="https://images.pexels.com/photos/16053029/pexels-photo-16053029.jpeg"
                  alt="Surveillance Control Room"
                  className="w-full h-80 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-near-black/60 to-transparent" />
              </div>
            </motion.div>
          </div>
        </div>
      </section> */}

      {/* CTA Section */}
      {/* <section className="section-padding bg-rich-burgundy/20">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="glass-morphism rounded-24 p-12 max-w-4xl mx-auto">
              <h2 className="heading-font text-4xl md:text-5xl font-bold mb-6 text-off-white">
                Secure Your <span className="text-royal-gold">Facility</span>
              </h2>
              
              <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                Protect your critical infrastructure with state-of-the-art surveillance 
                systems designed for government and enterprise security requirements.
              </p>
              
              <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-6">
                <Link to="/contact" className="cta-button">
                  Design Your System
                </Link>
                <Link to="/services" className="secondary-button">
                  View All Services
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section> */}
    </div>
  );
};

export default SurveillancePage;